import type { CSSProperties, FC, ReactElement } from 'react';

import type { IconList } from '@snowit/assets';
import type { A11y, IconColor } from '@snowit/icetypes';

import * as assets from '@snowit/assets';
import { COLOR } from '@snowit/icetypes';
import testId from '@snowit/utils/testId';
import validators from '@snowit/utils/validators';

import Svg from 'react-inlinesvg';

export interface IIcon {
  id?: string;
  className?: string;
  /**
   * The name of the icons, mapped from a collection of SVGs
   */
  name?: IconList;
  /**
   * Fallback in case the icon does not exist
   * or there is any problem in fetching the icon src
   */
  fallback?: IconList;
  color?: IconColor | string;
  sizeV2?: number | number[];
  style?: CSSProperties;
  emoji?: boolean;
  onClick?: (...args: any[]) => void;
  a11y?: A11y;
  /** Jest/Cypress test string */
  test?: string;
  /** @deprecated The props should not be used, use sizeV2 instead */
  size?: string | number;
}

const groupedIcons = {
  ...assets.SvgIcons,
  ...assets.SvgPayments,
  ...assets.SvgProducts,
  ...assets.SvgStatus,
  ...assets.SvgImages,
  ...assets.SvgLogos,
  ...assets.SvgLogosOthers,
  ...assets.SvgWeather,
  ...assets.SvgTenants,
  ...assets.SvgProviders,
  ...assets.SvgAnimated,
  ...assets.SvgFlags,
  ...assets.SvgRating,
};

/**
 * Icon component description
 */
const Icon: FC<IIcon> = (props: IIcon): ReactElement => {
  const { name, color = 'text', size, sizeV2, fallback, test, emoji, a11y, ...rest } = props;

  const sizes = (Array.isArray(sizeV2) ? sizeV2 : [sizeV2]).filter((sz): sz is number =>
    validators.isNumber(sz),
  );
  const src = groupedIcons[name ?? ''] || groupedIcons[fallback ?? ''];
  const width = sizes.length ? `${sizes[0]}rem` : size;
  const minWidth = sizes.length ? width : undefined;
  const height = sizes.length ? `${sizes[1] || sizes[0]}rem` : size;
  const minHeight = sizes.length ? height : undefined;
  const _color = COLOR.includes(color as IconColor) ? `var(--c-${color})` : color;

  if (emoji) return <span>{name}</span>;
  return (
    <Svg
      src={src ?? ''}
      width={width}
      height={height}
      fill={_color}
      style={{ minWidth: minWidth, minHeight: minHeight }}
      role={a11y?.role}
      aria-hidden={a11y?.hidden}
      aria-label={a11y?.label}
      {...testId(test)}
      {...rest}
    >
      {!src && 'x'}
    </Svg>
  );
};

export default Icon;
